import React from 'react';
import { Mail, X } from 'lucide-react';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useLanguage } from '../../contexts/LanguageContext';

interface ConfirmationDialogProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog = ({ title, message, onConfirm, onCancel }: ConfirmationDialogProps) => {
  const { t } = useLanguage();
  
  // Close dialog when Escape key is pressed
  useEscapeKey(onCancel);
  
  // Parse message to extract email info
  let confirmationText = message;
  let recipientLabel = "";
  let recipientEmail = "";
  
  if (message.includes("\\n\\n")) {
    const parts = message.split("\\n\\n");
    confirmationText = parts[0];
    
    if (parts[1]) {
      // Extract label and email separately
      const recipientParts = parts[1].split(":\\n");
      if (recipientParts.length === 2) {
        recipientLabel = recipientParts[0];
        recipientEmail = recipientParts[1];
      }
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60] p-4 animate-fadeIn">
      <div className="bg-white rounded-lg w-full max-w-md shadow-lg overflow-hidden border">
        {/* Header with close button only */}
        <div className="px-4 pt-2 pb-0 flex justify-end">
          <button 
            onClick={onCancel}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-5 pb-3">
          <h3 className="text-xl font-medium text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-700 mb-3">{confirmationText.replace(/\\n/g, ' ')}</p>
          
          {recipientEmail && (
            <div className="flex items-center p-2.5 bg-blue-50 rounded-md">
              <div className="mr-2.5 p-1.5 bg-blue-100 rounded-full">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <span className="text-sm text-gray-500">{recipientLabel}</span>
                <p className="text-sm font-medium text-gray-700">{recipientEmail}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 bg-blue-600 rounded text-sm font-medium text-white hover:bg-blue-700"
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;